import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import api from '../../api';
import { useLanguage, getLocale } from '../../contexts/LanguageContext';
import { useAccess } from '../../contexts/AccessContext';
import Icon from '../ui/Icon';
import ServerIcon from '../ui/ServerIcon';
import { fmtKamasLong, fmtDateTime } from '../auction/auctionUtils';
import BotCommandsModal from './BotCommandsModal';
import './AdminDashboard.css';

// Espace insécable classique : les polices n'ont pas toutes l'espace fine utilisée par Intl en français
const formatNumber = (n) => new Intl.NumberFormat(getLocale()).format(n || 0).replace(/\u202f/g, '\u00a0');

const formatDate = (iso) => new Date(iso).toLocaleDateString(getLocale(), { day: 'numeric', month: 'short' });

const StatTile = ({ icon, tone, label, value, detail }) => (
    <div className={`c2-tile dash-stat dash-stat--${tone}`}>
        <div className="dash-stat__label">
            <span className="dash-stat__icon"><Icon name={icon} size={18} /></span>
            {label}
        </div>
        <span className="dash-stat__value">{value}</span>
        {detail && <span className="dash-stat__detail">{detail}</span>}
    </div>
);

const CurrentSeason = ({ season, serverId, t, canManage }) => {
    if (!season) {
        return (
            <section className="c2-card dash-season dash-season--empty">
                <div className="dash-season__head">
                    <span className="c2-badge"><span className="c2-diamond c2-diamond--outline" />{t('adminHome.currentSeason', 'Saison en cours')}</span>
                    <h2>{t('adminHome.noSeason', 'Aucune saison en cours')}</h2>
                    <p className="c2-muted">{t('adminHome.noSeasonHint', 'Lance une saison pour démarrer un nouveau classement.')}</p>
                </div>
                {canManage && <Link to={`/server/${serverId}/saison?create=1`} className="c2-btn c2-btn--primary">
                    {t('adminHome.createSeason', 'Créer une saison')}
                </Link>}
            </section>
        );
    }

    const start = new Date(season.startDate).getTime();
    const end = new Date(season.endDate).getTime();
    const progress = Math.min(100, Math.max(0, ((Date.now() - start) / (end - start)) * 100));
    const remaining = season.daysLeft <= 1
        ? t('adminHome.lastDay', 'Dernier jour')
        : t('adminHome.daysLeft', '{n} jours restants').replace('{n}', season.daysLeft);

    return (
        <section className="c2-card dash-season">
            <div className="dash-season__main">
                <div className="dash-season__head">
                    <span className="c2-badge c2-badge--green"><span className="c2-diamond" />{t('adminHome.currentSeason', 'Saison en cours')}</span>
                    <h2>{season.title}</h2>
                    <p className="c2-muted">
                        {formatDate(season.startDate)} → {formatDate(season.endDate)} · {remaining}
                    </p>
                </div>
                <div className="dash-progress" role="progressbar" aria-valuenow={Math.round(progress)} aria-valuemin={0} aria-valuemax={100}>
                    <div className="dash-progress__bar" style={{ width: `${progress}%` }} />
                </div>
                <div className="dash-season__footer">
                    <span className="c2-muted">
                        {t('adminHome.rankedPlayers', '{n} joueurs classés').replace('{n}', season.activePlayers)}
                    </span>
                    {canManage && <Link to={`/server/${serverId}/saisons/${season.id}/edit`} className="c2-btn c2-btn--ghost c2-btn--sm">
                        {t('adminHome.viewSeason', 'Ouvrir la saison')}
                        <Icon name="arrowRight" size={16} />
                    </Link>}
                </div>
            </div>

            <div className="dash-podium">
                <span className="dash-podium__title">{t('adminHome.top3', 'Top 3')}</span>
                {season.top.length === 0 ? (
                    <p className="c2-muted">{t('adminHome.noRanking', 'Pas encore de points cette saison.')}</p>
                ) : (
                    <ol className="dash-podium__list">
                        {season.top.map((player, index) => (
                            <li key={player.joueurId} className={`dash-podium__row dash-podium__row--${index + 1}`}>
                                <span className="dash-podium__rank">{index + 1}</span>
                                <span className="dash-podium__name">{player.displayName || t('adminHome.unknownPlayer', 'Joueur inconnu')}</span>
                                <span className="dash-podium__points">{formatNumber(player.points)} {t('adminHome.pts', 'pts')}</span>
                            </li>
                        ))}
                    </ol>
                )}
            </div>
        </section>
    );
};

const StatusPill = ({ on, onLabel, offLabel }) => (
    <span className={`dash-pill${on ? ' is-on' : ''}`}>
        <span className="c2-diamond" />
        {on ? onLabel : offLabel}
    </span>
);

const StatusRow = ({ label, children }) => (
    <div className="dash-status__row">
        <span className="c2-muted">{label}</span>
        <span className="dash-status__value">{children}</span>
    </div>
);

const StatusCard = ({ icon, title, link, linkLabel, children }) => (
    <div className="c2-tile dash-status">
        <div className="dash-status__head">
            <span className="dash-stat__icon"><Icon name={icon} size={18} /></span>
            <h3>{title}</h3>
            {link && (
                <Link to={link} className="dash-status__link" aria-label={linkLabel} title={linkLabel}>
                    <Icon name="arrowRight" size={18} />
                </Link>
            )}
        </div>
        {children}
    </div>
);

const ReservationStatus = ({ reservation, serverId, canManage }) => {
    const { t } = useLanguage();
    const session = reservation.activeSession;
    const link = canManage
        ? (session ? `/server/${serverId}/reservation/${session.id}/edit` : `/server/${serverId}/reservation`)
        : null;

    return (
        <StatusCard icon="clipboard" title={t('adminHome.resaCard', 'Fiche de réservation')} link={link} linkLabel={t('adminHome.openSheet', 'Ouvrir la fiche')}>
            {!reservation.enabled ? (
                <StatusRow label={t('adminHome.reservations', 'Réservations')}><StatusPill on={false} offLabel={t('adminHome.disabled', 'Désactivées')} /></StatusRow>
            ) : !session ? (
                <p className="c2-muted">{t('adminHome.noActiveSheet', 'Aucune fiche active.')}</p>
            ) : (
                <>
                    <StatusRow label={t('adminHome.activeSheet', 'Fiche active')}>{session.title}</StatusRow>
                    <StatusRow label={t('adminHome.memberSwaps', 'Échanges entre membres')}>
                        <StatusPill on={session.swapWindowOpen} onLabel={t('adminHome.open', 'Ouverts')} offLabel={t('adminHome.closed', 'Fermés')} />
                    </StatusRow>
                    <StatusRow label={t('adminHome.assignments', 'Attributions')}>
                        <StatusPill on={!session.frozen} onLabel={t('adminHome.automatic', 'Automatiques')} offLabel={t('adminHome.frozen', 'Figées')} />
                    </StatusRow>
                </>
            )}
        </StatusCard>
    );
};

const AuctionStatus = ({ auction, serverId, canManage }) => {
    const { t } = useLanguage();
    const link = canManage
        ? (auction ? `/server/${serverId}/encheres/${auction.id}` : `/server/${serverId}/encheres`)
        : null;

    return (
        <StatusCard icon="hammer" title={t('navigation.auctions', 'Enchères')} link={link} linkLabel={t('adminHome.openAuctions', 'Ouvrir les enchères')}>
            {!auction ? (
                <StatusRow label={t('adminHome.status', 'Statut')}><StatusPill on={false} offLabel={t('adminHome.noAuction', 'Aucune enchère ouverte')} /></StatusRow>
            ) : (
                <>
                    <StatusRow label={auction.title}>
                        <StatusPill on={auction.running} onLabel={t('adminHome.auctionOpen', 'Ouvertes')} offLabel={t('adminHome.auctionToClose', 'Terminées, à clôturer')} />
                    </StatusRow>
                    <StatusRow label={auction.running ? t('adminHome.end', 'Fin') : t('adminHome.endedOn', 'Terminée le')}>{fmtDateTime(auction.endsAt)}</StatusRow>
                    <StatusRow label={t('adminHome.bidsTotal', 'Total des mises')}>{fmtKamasLong(auction.totalAmount)}</StatusRow>
                    <StatusRow label={t('adminHome.zonesWithBid', 'Zones avec une mise')}>
                        {auction.soldCount} / {auction.lotCount} · {auction.bidCount > 1 ? t('adminHome.bidsN', '{n} mises', { n: auction.bidCount }) : t('adminHome.bidN', '{n} mise', { n: auction.bidCount })}
                    </StatusRow>
                </>
            )}
        </StatusCard>
    );
};

/**
 * Tableau de bord d'un serveur : saison en cours, état des réservations et enchères, chiffres clés.
 */
const AdminDashboard = ({ server }) => {
    const { serverId } = useParams();
    const { t } = useLanguage();
    const { can } = useAccess();
    const [data, setData] = useState(null);
    const [error, setError] = useState(false);
    const [showCommands, setShowCommands] = useState(false);

    useEffect(() => {
        let cancelled = false;
        setData(null);
        setError(false);
        api.get(`/dashboard/${serverId}`)
            .then(res => { if (!cancelled) setData(res.data); })
            .catch(() => { if (!cancelled) setError(true); });
        return () => { cancelled = true; };
    }, [serverId]);

    return (
        <div className="c2-scope dash">
            <header className="dash-header c2-fade-up">
                <ServerIcon guild={server} size={64} />
                <div className="dash-header__title">
                    <p className="c2-muted">{t('navigation.dashboard')}</p>
                    <h1>{server?.name}</h1>
                </div>
                <button type="button" className="c2-btn c2-btn--ghost c2-btn--sm" onClick={() => setShowCommands(true)}>
                    <Icon name="terminal" size={16} />
                    {t('botCommands.title', 'Commandes du bot')}
                </button>
            </header>

            {showCommands && <BotCommandsModal onClose={() => setShowCommands(false)} />}

            {error && <p className="dash-error" role="alert">{t('adminHome.statsError', 'Impossible de charger les chiffres du serveur.')}</p>}

            {!data && !error && (
                <div className="dash-loading"><div className="c2-spinner" /></div>
            )}

            {data && (
                <>
                    <CurrentSeason season={data.currentSeason} serverId={serverId} t={t} canManage={can(serverId, 'saisons')} />

                    <section className="dash-section">
                        <h2 className="dash-section__title">{t('adminHome.rightNow', 'En ce moment')}</h2>
                        <div className="dash-stats">
                            <ReservationStatus reservation={data.reservation} serverId={serverId} canManage={can(serverId, 'reservation')} />
                            <AuctionStatus auction={data.auction} serverId={serverId} canManage={can(serverId, 'encheres')} />
                        </div>
                    </section>

                    <section className="dash-section">
                        <h2 className="dash-section__title">{t('adminHome.statsTitle', 'Le serveur en chiffres')}</h2>
                        <div className="dash-stats">
                            <StatTile
                                icon="gem"
                                tone="purple"
                                label={t('adminHome.pepitesCurrent', 'Pépites en cours')}
                                value={formatNumber(data.pepites.current)}
                                detail={t('adminHome.pepitesCurrentDetail', 'Depuis la dernière distribution')}
                            />
                            <StatTile
                                icon="gem"
                                tone="green"
                                label={t('adminHome.pepites', 'Pépites récoltées')}
                                value={formatNumber(data.pepites.total)}
                                detail={t('adminHome.pepitesTotalDetail', 'Au total, distributions comprises')}
                            />
                        </div>
                    </section>
                </>
            )}
        </div>
    );
};

export default AdminDashboard;
